import Swal from 'sweetalert2';
import { BRAND } from '../../brand';

// ============================================================================
//  claimsDialog — the ask-before-you-act dialogs for the Claims screens
// ============================================================================
//
//  WHAT THIS REPLACES
//
//  Seven `window.confirm` / `window.alert` calls across the claim editor and its
//  four row tables. The browser's own dialog is the wrong control for this work
//  for reasons that are not only cosmetic:
//
//    · It says "localhost:3000 says" above the message. On a hospital's own
//      installation that reads as a browser warning, not as the system asking a
//      question, and it puts the server's hostname in front of the user.
//    · Everything is one blob of monospace-ish text. The save confirmation
//      lists the warnings the server returned — a visit date moving the claim
//      into another month, a folder number that will drag its medications with
//      it — and in a native dialog those are indistinguishable from the
//      question itself.
//    · Its buttons are "OK" and "Cancel" whatever is about to happen. Removing
//      a diagnosis and saving four edits get identical, unlabelled affordances.
//    · It cannot be styled, so it is the one part of the product that never
//      matched the product.
//
//  SweetAlert2 rather than a bespoke React dialog: it is already a dependency
//  and already the confirmation used by Setup, Insurance, User Roles and patient
//  registration, so this brings Claims onto the same control as the rest of the
//  system rather than adding a fourth idea of what a dialog looks like.
//
//  Everything here is promise-based and returns a plain boolean, so a call site
//  keeps the shape it already had:
//
//      if (!window.confirm('…')) return;        →
//      if (!(await confirmClaim({ … }))) return;

// ── Styling ─────────────────────────────────────────────────────────────────
//
//  Injected once, on first use, rather than imported as a stylesheet: this file
//  is the only thing that needs it, and a component that carries its own styling
//  cannot be half-installed. Keyed on the id so React's fast refresh cannot
//  stack a second copy.
const STYLE_ID = 'mefims-claims-dialog-styles';

const ensureStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;

  const css = `
.mf-pop {
  border-radius: 14px !important;
  padding: 0 0 4px !important;
  border: 1px solid ${BRAND.line} !important;
  box-shadow: 0 24px 60px rgba(15,23,42,.28) !important;
  font-family: inherit !important;
  width: 32em !important;
  max-width: calc(100vw - 32px) !important;
}
.mf-title {
  font-size: 16px !important;
  font-weight: 800 !important;
  color: ${BRAND.ink} !important;
  padding: 20px 22px 0 !important;
  margin: 0 !important;
  text-align: left !important;
  line-height: 1.35 !important;
}
.mf-html {
  font-size: 13px !important;
  color: ${BRAND.inkSoft} !important;
  text-align: left !important;
  margin: 0 !important;
  padding: 8px 22px 4px !important;
  line-height: 1.6 !important;
}
.mf-actions {
  padding: 6px 22px 20px !important;
  margin: 0 !important;
  justify-content: flex-end !important;
  gap: 8px !important;
}
.mf-btn, .mf-ghost {
  border-radius: 9px !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  padding: 9px 18px !important;
  margin: 0 !important;
  box-shadow: none !important;
  transition: filter .15s ease, background .15s ease !important;
}
.mf-btn:focus-visible, .mf-ghost:focus-visible {
  outline: 2px solid ${BRAND.navy} !important;
  outline-offset: 2px !important;
}
.mf-btn:hover { filter: brightness(1.07) !important; }
.mf-ghost {
  background: #fff !important;
  color: ${BRAND.inkMuted} !important;
  border: 1px solid ${BRAND.line} !important;
}
.mf-ghost:hover {
  background: ${BRAND.canvas} !important;
  color: ${BRAND.ink} !important;
}
/*  The rows inside the body: what will change, and what to watch out for. */
.mf-list { margin: 12px 0 0; padding: 0; list-style: none; }
.mf-list li {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 7px 10px; margin-top: 5px;
  border-radius: 8px; font-size: 12.5px; line-height: 1.5;
  background: ${BRAND.canvas};
  border: 1px solid ${BRAND.line};
  color: ${BRAND.ink};
}
.mf-list li.mf-warn {
  background: #FFF8EC;
  border-color: rgba(224,139,47,.32);
  color: #7A4E12;
}
.mf-list li b { font-weight: 800; }
.mf-dot {
  flex: 0 0 auto; width: 6px; height: 6px; border-radius: 50%;
  margin-top: 7px; background: ${BRAND.navy};
}
.mf-warn .mf-dot { background: ${BRAND.warning}; }
.mf-note {
  margin-top: 12px; font-size: 12px; color: ${BRAND.inkMuted};
}
.mf-strong { color: ${BRAND.ink}; font-weight: 700; }
`;

  const tag = document.createElement('style');
  tag.id = STYLE_ID;
  tag.appendChild(document.createTextNode(css));
  document.head.appendChild(tag);
};

//  Everything below interpolates claim data — diagnosis descriptions, drug
//  names, folder numbers — into HTML. All of it is escaped: these strings come
//  from an imported XML file and a hospital's own typing, and neither is a
//  trusted source of markup.
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

const TONES = {
  //  The ordinary "yes, do it" — the product's green.
  primary: { confirm: BRAND.green },
  //  Removal and anything else that cannot be undone.
  danger:  { confirm: BRAND.danger },
};

// ── confirmClaim ────────────────────────────────────────────────────────────
//
//    title        the question, in one line
//    lead         a sentence of context under it            (optional)
//    items        [{ text, warn }] — what will change, or what to watch for
//    note         a closing line, quieter than the rest      (optional)
//    confirmText  names the ACTION rather than saying "OK", so the button says
//                 what it does even when the title has been skim-read
//    tone         'primary' | 'danger'
//
//  Resolves true only when the confirm button was pressed. Escape, the backdrop
//  and Cancel all resolve false, so a dismissed dialog can never be read as
//  consent.
export const confirmClaim = async ({
  title,
  lead,
  items = [],
  note,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  tone = 'primary',
} = {}) => {
  ensureStyles();

  const rows = (items || []).filter(Boolean);
  const html = [
    lead ? `<div>${esc(lead)}</div>` : '',
    rows.length
      ? `<ul class="mf-list">${rows.map((it) => {
          const text = typeof it === 'string' ? it : it.text;
          const warn = typeof it === 'object' && it.warn;
          return `<li class="${warn ? 'mf-warn' : ''}">`
               + '<span class="mf-dot"></span>'
               + `<span>${esc(text)}</span></li>`;
        }).join('')}</ul>`
      : '',
    note ? `<div class="mf-note">${esc(note)}</div>` : '',
  ].filter(Boolean).join('');

  const res = await Swal.fire({
    title,
    html: html || undefined,
    //  No icon. The title says what is being asked and the button says what it
    //  does; a large exclamation mark above both is decoration that makes a
    //  routine save look like a failure.
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    confirmButtonColor: (TONES[tone] || TONES.primary).confirm,
    reverseButtons: true,          // confirm on the right, where the eye ends
    focusCancel: tone === 'danger',
    buttonsStyling: true,
    customClass: {
      popup: 'mf-pop',
      title: 'mf-title',
      htmlContainer: 'mf-html',
      actions: 'mf-actions',
      confirmButton: 'mf-btn',
      cancelButton: 'mf-ghost',
    },
  });

  return res.isConfirmed === true;
};

// ── notifyClaim ─────────────────────────────────────────────────────────────
//
//  The one-way message that was `window.alert`. Same chrome, one button.
export const notifyClaim = async ({
  title,
  lead,
  items = [],
  note,
  confirmText = 'Got it',
} = {}) => {
  ensureStyles();

  const rows = (items || []).filter(Boolean);
  const html = [
    lead ? `<div>${esc(lead)}</div>` : '',
    rows.length
      ? `<ul class="mf-list">${rows.map((it) => {
          const text = typeof it === 'string' ? it : it.text;
          return '<li><span class="mf-dot"></span>'
               + `<span>${esc(text)}</span></li>`;
        }).join('')}</ul>`
      : '',
    note ? `<div class="mf-note">${esc(note)}</div>` : '',
  ].filter(Boolean).join('');

  await Swal.fire({
    title,
    html: html || undefined,
    confirmButtonText: confirmText,
    confirmButtonColor: BRAND.green,
    buttonsStyling: true,
    customClass: {
      popup: 'mf-pop',
      title: 'mf-title',
      htmlContainer: 'mf-html',
      actions: 'mf-actions',
      confirmButton: 'mf-btn',
    },
  });
};

// ── confirmRemove ───────────────────────────────────────────────────────────
//
//  The four row tables — diagnoses, procedures, investigations, medications —
//  all asked the same question in the same words, so they ask it from here
//  instead of from four copies that would drift.
export const confirmRemove = ({ what, name }) => confirmClaim({
  title: `Remove this ${what}?`,
  lead: name ? `${name} will be taken off this claim.` : undefined,
  note: 'The claim total is recalculated when it is removed.',
  confirmText: 'Remove',
  tone: 'danger',
});
